import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth.jsx';
import { useBranding, useBrandingMutations } from '../../hooks/useBranding.js';
import { PhotoUpload } from '../../components/common/PhotoUpload.jsx';
import { VerifyPreviewCard } from '../../components/verify/VerifyPreviewCard.jsx';
import { ApiError } from '../../lib/api.js';

/**
 * Branding settings.
 *
 * Two columns: form on the left, live preview on the right. Preview
 * updates as the admin types — no save-required to see how changes look.
 * Saving persists via PUT /api/dashboard/branding.
 *
 * Logo upload is a separate endpoint (POST /api/dashboard/branding/logo,
 * multipart) so it uploads immediately on file-pick, not on form save.
 * That matches how staff photos work in Chunk 2B and keeps the mental
 * model consistent: photos = upload-on-pick, text fields = save-on-click.
 *
 * The live preview uses a fake staff record so admins can see what the
 * verify page looks like without having to pick a real staff member:
 *
 *   - "Verified" preview by default (the most common state admins
 *     want to see)
 *   - Clickable state-switcher lets them preview under_review,
 *     no_longer_employed, unknown without having to actually change
 *     any real staff records
 *
 * 2026-05-21: Added "Verification status messages" section — four optional
 * per-status custom messages that override the default verify-card copy.
 * Live preview reflects the in-progress custom message for the selected
 * preview state so admins see what the public scanner will see.
 */

const PREVIEW_STAFF = {
  display_name: 'Dani Morgan',
  role: 'Senior Cleaner',
  photo_url: null, // uses initials fallback so preview works without a real photo
};

const PREVIEW_STATES = [
  { key: 'verified', label: 'Verified' },
  { key: 'under_review', label: 'Under review' },
  { key: 'no_longer_employed', label: 'No longer employed' },
  { key: 'unknown', label: 'Unknown' },
];

const DISPLAY_MODES = [
  { key: 'full',       label: 'Full name',         example: 'Dani Morgan' },
  { key: 'initial',    label: 'First + initial',   example: 'Dani M.' },
  { key: 'first_only', label: 'First only',        example: 'Dani' },
];

const VERIFY_MESSAGE_FIELDS = [
  {
    field: 'verify_message_verified',
    label: 'Verified',
    hint: 'Shown when a staff QR code resolves to an active employee in good standing.',
    placeholder: 'e.g. This person is a fully-vetted member of our cleaning team.',
  },
  {
    field: 'verify_message_under_review',
    label: 'Under review',
    hint: 'Shown when a staff member is temporarily flagged, or when your subscription is suspended.',
    placeholder: 'e.g. This person\'s access is temporarily on hold. Please call us before letting them on site.',
  },
  {
    field: 'verify_message_no_longer_employed',
    label: 'No longer employed',
    hint: 'Shown for ex-staff. Used when someone scans an old badge.',
    placeholder: 'e.g. This person no longer works for our organisation.',
  },
  {
    field: 'verify_message_unknown',
    label: 'Unknown code',
    hint: 'Shown when the QR code does not match any staff record in your account.',
    placeholder: 'e.g. This code is not a recognised staff badge for our organisation.',
  },
];

const MAX_VERIFY_MESSAGE_LENGTH = 300;

export function BrandingPage() {
  const { tenant } = useAuth();
  const { branding, loading, error, refresh } = useBranding();
  const { updateBranding, uploadLogo, deleteLogo } = useBrandingMutations();

  const [form, setForm] = useState({
    brand_color: '',
    brand_color_dark: '',
    public_tagline: '',
    public_phone: '',
    public_website: '',
    display_name_mode: 'full',
    verify_message_verified: '',
    verify_message_under_review: '',
    verify_message_no_longer_employed: '',
    verify_message_unknown: '',
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [previewState, setPreviewState] = useState('verified');

  // Hydrate form from branding data
  useEffect(() => {
    if (branding) {
      setForm({
        brand_color: branding.brand_color || '',
        brand_color_dark: branding.brand_color_dark || '',
        public_tagline: branding.public_tagline || '',
        public_phone: branding.public_phone || '',
        public_website: branding.public_website || '',
        display_name_mode: branding.display_name_mode || 'full',
        verify_message_verified: branding.verify_message_verified || '',
        verify_message_under_review: branding.verify_message_under_review || '',
        verify_message_no_longer_employed: branding.verify_message_no_longer_employed || '',
        verify_message_unknown: branding.verify_message_unknown || '',
      });
      setDirty(false);
    }
  }, [branding]);

  // Warn on unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  function setField(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setDirty(true);
    setSaveSuccess(false);
  }

  function setVerifyMessage(field, raw) {
    // Defensive client-side clamp to 300 chars matching backend limit
    const clamped = typeof raw === 'string' ? raw.slice(0, MAX_VERIFY_MESSAGE_LENGTH) : '';
    setField(field, clamped);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaveError(null);
    setSaveSuccess(false);

    // Basic hex validation before sending
    const hex = /^#[0-9a-fA-F]{6}$/;
    if (form.brand_color && !hex.test(form.brand_color)) {
      setSaveError('Brand colour must be a hex value like #00AB39.');
      return;
    }
    if (form.brand_color_dark && !hex.test(form.brand_color_dark)) {
      setSaveError('Secondary colour must be a hex value like #008A2E.');
      return;
    }

    setSaving(true);
    try {
      await updateBranding({
        brand_color: form.brand_color || null,
        brand_color_dark: form.brand_color_dark || null,
        public_tagline: form.public_tagline || null,
        public_phone: form.public_phone || null,
        public_website: form.public_website || null,
        display_name_mode: form.display_name_mode,
        verify_message_verified: form.verify_message_verified || null,
        verify_message_under_review: form.verify_message_under_review || null,
        verify_message_no_longer_employed: form.verify_message_no_longer_employed || null,
        verify_message_unknown: form.verify_message_unknown || null,
      });
      setDirty(false);
      setSaveSuccess(true);
      refresh();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoUpload(file) {
    await uploadLogo(file);
    refresh();
  }

  async function handleLogoDelete() {
    await deleteLogo();
    refresh();
  }

  // Preview data — merge live form values with a fake staff record.
  // Pass tenant.verify_message_* into the tenant prop so the preview
  // card sees the in-progress custom copy for the selected state.
  const previewData = useMemo(() => {
    const staff = previewState === 'unknown' ? null : { ...PREVIEW_STAFF };
    // Apply display_name_mode to preview
    if (staff && form.display_name_mode === 'initial') {
      staff.display_name = 'Dani M.';
    } else if (staff && form.display_name_mode === 'first_only') {
      staff.display_name = 'Dani';
    }

    // Build the message exactly as the backend would: tenant override if set,
    // otherwise the default for that state.
    const customMessage = form[`verify_message_${previewState}`];
    const tenantName = tenant?.name || branding?.name || 'Your Organisation';
    const message = customMessage && customMessage.trim().length > 0
      ? customMessage.trim()
      : defaultMessageFor(previewState, tenantName);

    return {
      result: previewState,
      message,
      tenant: {
        name: tenantName,
        logo_url: branding?.logo_url || null,
        brand_color: form.brand_color || null,
        brand_color_dark: form.brand_color_dark || null,
        tagline: form.public_tagline || null,
        phone: form.public_phone || null,
        website: form.public_website || null,
      },
      staff,
    };
  }, [previewState, form, branding, tenant]);

  if (loading) {
    return (
      <div style={{ padding: 'var(--sp-10) 0', textAlign: 'center' }}>
        <div className="spinner" aria-hidden="true" />
        <p className="muted" style={{ marginTop: 'var(--sp-3)' }}>Loading branding…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error" role="alert">
        <strong>Couldn't load branding settings.</strong>{' '}
        <button type="button" className="btn btn-ghost" onClick={refresh} style={{ padding: 0, textDecoration: 'underline' }}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title-wrap">
          <h1 className="page-title">Branding</h1>
          <p className="page-subtitle">
            How your verify page looks to members of the public when they
            scan a staff QR code. Updates go live instantly.
          </p>
        </div>
      </div>

      {saveError && <div className="alert alert-error" role="alert">{saveError}</div>}
      {saveSuccess && (
        <div className="alert alert-success" role="status">
          Branding saved. Your verify pages are updated.
        </div>
      )}

      <div className="branding-layout">
        <form onSubmit={handleSave} className="branding-form-card" noValidate>
          {/* Logo */}
          <fieldset className="editor-section" style={{ border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)' }}>
            <legend className="editor-section-title">Logo</legend>
            <PhotoUpload
              photoUrl={branding?.logo_url}
              initials={(tenant?.name || 'T').split(/\s+/).map(w => w.charAt(0)).filter(Boolean).slice(0, 2).join('').toUpperCase()}
              onUpload={handleLogoUpload}
              onDelete={branding?.logo_url ? handleLogoDelete : undefined}
            />
          </fieldset>

          {/* Colours */}
          <fieldset className="editor-section" style={{ border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)' }}>
            <legend className="editor-section-title">Colour</legend>

            <div className="field">
              <label className="field-label" htmlFor="f-brand-color">Brand colour</label>
              <div className="field-hint">Used on the verify page for your "Verified" badge and accents.</div>
              <ColorField
                id="f-brand-color"
                value={form.brand_color}
                onChange={v => setField('brand_color', v)}
              />
            </div>

            <div className="field">
              <label className="field-label" htmlFor="f-brand-color-dark">Secondary colour</label>
              <div className="field-hint">Optional. Used for pressed/hover states if your primary colour is light.</div>
              <ColorField
                id="f-brand-color-dark"
                value={form.brand_color_dark}
                onChange={v => setField('brand_color_dark', v)}
              />
            </div>
          </fieldset>

          {/* Tagline */}
          <fieldset className="editor-section" style={{ border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)' }}>
            <legend className="editor-section-title">Message</legend>

            <div className="field">
              <label className="field-label" htmlFor="f-tagline">Tagline</label>
              <div className="field-hint">Short line shown under your logo on the verify page. E.g. "Vetted. Uniformed. Verified."</div>
              <input
                id="f-tagline"
                className="field-input"
                type="text"
                value={form.public_tagline}
                onChange={e => setField('public_tagline', e.target.value)}
                maxLength={120}
              />
              <div className="field-hint" style={{ marginTop: 'var(--sp-1)', textAlign: 'right' }}>
                {form.public_tagline.length}/120
              </div>
            </div>
          </fieldset>

          {/* Contact */}
          <fieldset className="editor-section" style={{ border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)' }}>
            <legend className="editor-section-title">Public contact</legend>
            <div className="field-hint" style={{ marginBottom: 'var(--sp-4)' }}>
              Shown at the bottom of the verify page. So anyone scanning a
              QR code can reach you if they have questions.
            </div>

            <div className="field-row">
              <div className="field">
                <label className="field-label" htmlFor="f-phone">Phone</label>
                <input
                  id="f-phone"
                  className="field-input"
                  type="tel"
                  value={form.public_phone}
                  onChange={e => setField('public_phone', e.target.value)}
                  autoComplete="tel"
                  placeholder="01437 980118"
                />
              </div>
              <div className="field">
                <label className="field-label" htmlFor="f-website">Website</label>
                <input
                  id="f-website"
                  className="field-input"
                  type="url"
                  value={form.public_website}
                  onChange={e => setField('public_website', e.target.value)}
                  autoComplete="url"
                  placeholder="https://coastalcleaning.org.uk"
                />
              </div>
            </div>
          </fieldset>

          {/* Verification status messages — added 2026-05-21 */}
          <fieldset className="editor-section verify-messages-fieldset" style={{ border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)' }}>
            <legend className="editor-section-title">Verification status messages</legend>
            <div className="field-hint" style={{ marginBottom: 'var(--sp-4)' }}>
              Optional custom copy shown on the public verify page under the
              staff photo and name. Clear a field to fall back to the default
              copy. Maximum {MAX_VERIFY_MESSAGE_LENGTH} characters each.
            </div>

            {VERIFY_MESSAGE_FIELDS.map(({ field, label, hint, placeholder }) => {
              const value = form[field];
              const length = value.length;
              const atLimit = length >= MAX_VERIFY_MESSAGE_LENGTH;
              const id = `f-${field.replace(/_/g, '-')}`;
              return (
                <div className="field" key={field}>
                  <label className="field-label" htmlFor={id}>{label}</label>
                  <div className="field-hint">{hint}</div>
                  <textarea
                    id={id}
                    className="field-input verify-message-textarea"
                    value={value}
                    onChange={e => setVerifyMessage(field, e.target.value)}
                    maxLength={MAX_VERIFY_MESSAGE_LENGTH}
                    placeholder={placeholder}
                    rows={3}
                  />
                  <div
                    className="field-hint"
                    style={{
                      marginTop: 'var(--sp-1)',
                      textAlign: 'right',
                      color: atLimit ? 'var(--id-red-d)' : undefined,
                    }}
                  >
                    {length}/{MAX_VERIFY_MESSAGE_LENGTH}
                  </div>
                </div>
              );
            })}
          </fieldset>

          {/* Display mode */}
          <fieldset className="editor-section" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="editor-section-title">Staff name privacy</legend>
            <div className="field-hint" style={{ marginBottom: 'var(--sp-3)' }}>
              How staff names appear on the public verify page. You can
              still see full names in your dashboard.
            </div>
            <div className="display-mode-options" role="radiogroup" aria-label="Display mode">
              {DISPLAY_MODES.map(m => (
                <button
                  key={m.key}
                  type="button"
                  role="radio"
                  aria-checked={form.display_name_mode === m.key}
                  aria-pressed={form.display_name_mode === m.key}
                  className="display-mode-option"
                  onClick={() => setField('display_name_mode', m.key)}
                >
                  <h4 className="display-mode-label">{m.label}</h4>
                  <p className="display-mode-example">Shows as "{m.example}"</p>
                </button>
              ))}
            </div>
          </fieldset>

          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 'var(--sp-6)',
            paddingTop: 'var(--sp-5)',
            borderTop: '1px solid var(--line-soft)',
            gap: 'var(--sp-3)',
          }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || !dirty}
              aria-busy={saving}
            >
              {saving
                ? (<><span className="spinner" aria-hidden="true" /> Saving…</>)
                : 'Save changes'}
            </button>
          </div>
        </form>

        <aside className="branding-preview">
          <div className="preview-label">Preview — verify page</div>

          <div style={{ display: 'flex', gap: 'var(--sp-1)', flexWrap: 'wrap', marginBottom: 'var(--sp-4)' }}>
            {PREVIEW_STATES.map(s => (
              <button
                key={s.key}
                type="button"
                className={`toolbar-filter ${previewState === s.key ? 'active' : ''}`}
                style={{ fontSize: 'var(--fs-xs)', padding: '0.35rem 0.7rem' }}
                onClick={() => setPreviewState(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>

          <VerifyPreviewCard data={previewData} />

          <p className="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-4)', textAlign: 'center' }}>
            Live preview. Not a real scan — won't log anything.
          </p>
        </aside>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────── */

/**
 * Default verify-card message templates. Mirrors the backend defaults in
 * api/src/utils/verify-messages.js so that the live preview shows exactly
 * what the public verify page will show when a tenant hasn't set custom
 * copy. {tenant_name} is interpolated; custom messages are not.
 */
const DEFAULT_PREVIEW_MESSAGES = {
  verified: 'This person is currently verified by {tenant_name}.',
  under_review: "This person's status is currently under review. Please contact {tenant_name} before allowing access.",
  no_longer_employed: 'This person is no longer listed as currently employed by {tenant_name}.',
  unknown: 'This QR code does not match a current staff record. Please contact the organisation directly.',
};

function defaultMessageFor(result, tenantName) {
  const template = DEFAULT_PREVIEW_MESSAGES[result] || DEFAULT_PREVIEW_MESSAGES.unknown;
  return template.replace(/\{tenant_name\}/g, tenantName);
}

function ColorField({ id, value, onChange }) {
  // Synchronise the hex input with the colour picker. If the hex input is
  // partial (e.g. "#00A") the colour picker gets its last-valid value.
  const safeHex = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#00AB39';
  return (
    <div className="color-input-row">
      <label className="color-swatch" aria-label="Choose colour">
        <input
          type="color"
          value={safeHex}
          onChange={e => onChange(e.target.value.toUpperCase())}
        />
      </label>
      <input
        id={id}
        className="color-hex-input"
        type="text"
        value={value || ''}
        onChange={e => {
          let v = e.target.value.toUpperCase();
          if (v && !v.startsWith('#')) v = '#' + v;
          onChange(v.slice(0, 7));
        }}
        placeholder="#00AB39"
        maxLength={7}
      />
    </div>
  );
}
